import { useEffect, useState } from "react";
import { Bell, BellOff, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { pushSupported, isIosNotInstalled, getPushSubscription, subscribeToPush, unsubscribeFromPush } from "@/lib/push";

export default function NotificationSettings() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    if (!supported) return;
    getPushSubscription().then((sub) => setEnabled(!!sub)).catch(() => {});
  }, [supported]);

  const toggle = async () => {
    setLoading(true);
    try {
      if (enabled) {
        await unsubscribeFromPush();
        setEnabled(false);
        toast.success("Notifiche disattivate");
      } else {
        await subscribeToPush();
        setEnabled(true);
        toast.success("Notifiche attivate! Riceverai i promemoria degli eventi.");
      }
    } catch (e) {
      toast.error(e.message || "Impossibile attivare le notifiche");
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Notifica di prova inviata!");
    } catch {
      toast.error("Invio fallito. Riattiva le notifiche e riprova.");
    }
  };

  return (
    <div className="bg-surface border border-subtle rounded-3xl p-6 mb-4" data-testid="notification-settings-card">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-5 h-5 text-brand" />
        <h2 className="font-display text-lg font-medium">Notifiche promemoria</h2>
      </div>
      <p className="text-sm text-secondary-fg leading-relaxed mb-4">
        Ricevi una notifica sul telefono <strong>1 giorno prima</strong> e <strong>1 ora prima</strong> di ogni evento in agenda.
      </p>
      {!supported ? (
        <p className="text-xs text-tertiary-fg">Il tuo browser non supporta le notifiche push.</p>
      ) : isIosNotInstalled() ? (
        <p className="text-xs text-amber-warm">
          Su iPhone: aggiungi prima Family Brain alla schermata Home (Condividi → Aggiungi a Home), poi apri l'app da lì per attivare le notifiche.
        </p>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={toggle}
            disabled={loading}
            data-testid="notifications-toggle"
            className={`flex-1 flex items-center justify-center gap-2 ${enabled ? "btn-secondary" : "btn-primary"}`}
          >
            {enabled ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            {loading ? "Attendi..." : enabled ? "Disattiva notifiche" : "Attiva notifiche"}
          </button>
          {enabled && (
            <button onClick={sendTest} data-testid="notifications-test" className="btn-secondary !px-4 flex items-center gap-2">
              <Send className="w-4 h-4" /> Prova
            </button>
          )}
        </div>
      )}
    </div>
  );
}
